import type { ReactI13nPlugin } from 'react-i13n';
import Bugsnag from '@bugsnag/js';

import { I13N_TYPE_EXCEPTION, I13N_TYPE_LOG, I13N_TYPE_SET_VALUE } from 'Lib/constants/i13nSpec';
import { debugI13nBugsnag as debug, debugI13nBugsnagInfo as debugInfo } from 'Lib/debug';
import { Log } from 'Lib/errors';
import type { I13nPluginClass } from 'Lib/i13n';
import requestIdleCallbackManager from 'Lib/requestIdleCallbackManager';

class ReactI13nBugsnag implements I13nPluginClass {
  getPlugin = () => {
    if (!__CLIENT__) {
      // TODO: handle properly or don't call in server context
      return {} as ReactI13nPlugin;
    }

    return {
      name: 'bugsnag',
      eventHandlers: {
        [I13N_TYPE_EXCEPTION]: (payload) => {
          if (!__CLIENT__ || !Bugsnag.isStarted()) {
            return;
          }

          const {
            category,
            message,
            error: { error = {}, tags = [], ...rest } = {},
          } = payload ?? {};

          error.misc = {
            ...rest,
            code: error.error?.code,
            message,
            tags,
            type: category,
          };

          debug('Sending an exception', error);
          debugInfo('BugSnag exception', error);
          requestIdleCallbackManager.addTask(() => {
            Bugsnag.notify(error);
          });
        },

        [I13N_TYPE_LOG]: ({ log }: { log: Log }) => {
          if (!__CLIENT__ || !Bugsnag.isStarted()) {
            return;
          }

          debug('Sending a log', log);
          debugInfo('BugSnag log', log);
          requestIdleCallbackManager.addTask(() => {
            Bugsnag.notify(log, (report) => {
              // Set the severity as info for easily filtering the logs from the other errors on the BugSnag dashboard
              report.severity = 'info';
            });
          });
        },

        [I13N_TYPE_SET_VALUE]: ({ key, value }: { key: string; value: string }) => {
          if (!__CLIENT__ || !Bugsnag.isStarted()) {
            return;
          }

          switch (key) {
            case 'user_id': {
              debug('Set user id to', value);
              // Do not use addMetadata to set the id as it will not overwrite BugSnag's generated id
              Bugsnag.setUser(value);
              break;
            }
            case 'user_sub': {
              debug('Set user subscription to', value);
              Bugsnag.addMetadata('user', {
                subscriptionLevel: value,
              });
              break;
            }
            case 'user_saved_payment_methods': {
              debug('Set user payment methods to', value);
              Bugsnag.addMetadata('user', {
                savedPaymentMethods: value,
              });
              break;
            }
          }
        },
      },
    };
  };
}

export default ReactI13nBugsnag;
